import { gql } from '@apollo/client';
import {
  formatFullName,
  NursingOverviewType,
  ResidentWithNursingOverview,
  WeightOutOfRangeType,
} from '@dokucoach/common-types';
import { FilterValues } from 'components/FilterCheckBox';
import { TFunction } from 'i18next';
import { nanoid } from 'nanoid';
import { dateFormat } from 'utils/dateFormat';

const nursingOverviewQuery = `
  date
  type
  information
`;

export const GET_NURSING_OVERVIEW_LIST = gql`
    fragment CarePlans on NursingOverviewDto {
        carePlans {
            ${nursingOverviewQuery}
        }
    }

    fragment Wounds on NursingOverviewDto {
        wounds {
            ${nursingOverviewQuery}
        }
    }

    fragment Falls on NursingOverviewDto {
        falls {
            ${nursingOverviewQuery}
        }
    }

    fragment Pains on NursingOverviewDto {
        pains {
            ${nursingOverviewQuery}
        }
    }

    fragment Medications on NursingOverviewDto {
        medications(type: $includeMedicationsType) {
            ${nursingOverviewQuery}
        }
    }

    fragment NursingReports on NursingOverviewDto {
        nursingReports {
            ${nursingOverviewQuery}
        }
    }

    fragment VisitSheets on NursingOverviewDto {
        visitSheets(type: $includeVisitSheetsType) {
            ${nursingOverviewQuery}
        }
    }

    fragment NutritionProtocols on NursingOverviewDto {
        nutritionProtocols {
            ${nursingOverviewQuery}
        }
    }

    fragment VitalSigns on NursingOverviewDto {
        vitalSigns {
            ${nursingOverviewQuery}
        }
    }

    fragment VitalSignsHistory on NursingOverviewDto {
        vitalSignsHistory {
            ${nursingOverviewQuery}
        }
    }

    fragment SocialCareReports on NursingOverviewDto {
      socialCareReports {
          ${nursingOverviewQuery}
      }
  }

    query getNursingOverviewList($from: DateTime!,
        $to: DateTime!,
        $livingAreaId: ID!,
        $includeCarePlans: Boolean!,
        $includeWounds: Boolean!,
        $includeFalls: Boolean!,
        $includePains: Boolean!,
        $includeMedications: Boolean!,
        $includeMedicationsType: NursingOverviewMedicationType,
        $includeNursingReports: Boolean!,
        $includeVisitSheets: Boolean!,
        $includeVisitSheetsType: NursingOverviewVisitSheetType,
        $includeNutritionProtocols: Boolean!,
        $includeVitalSigns: Boolean!,
        $includeVitalSignsHistory: Boolean!,
        $includeSocialCareReports: Boolean!,
    ) {
        getResidents(livingAreaId: $livingAreaId) {
            id
            name {
                last
                firstFull
                title
            }
            room {
                name
            }
            livingArea {
                name
            }
            nursingOverview(from: $from, to: $to) {
                ...CarePlans @include(if: $includeCarePlans)
                ...Wounds @include(if: $includeWounds)
                ...Falls @include(if: $includeFalls)
                ...Pains @include(if: $includePains)
                ...Medications @include(if: $includeMedications)
                ...NursingReports @include(if: $includeNursingReports)
                ...VisitSheets @include(if: $includeVisitSheets)
                ...NutritionProtocols @include(if: $includeNutritionProtocols)
                ...VitalSigns @include(if: $includeVitalSigns)
                ...VitalSignsHistory @include(if: $includeVitalSignsHistory)
                ...SocialCareReports @include(if: $includeSocialCareReports)
            }
        }
    }
`;

export interface NursingOverviewRow {
  residentId: string;
  uniqueKey: string;
  residentInfo: string;
  date: Date;
  type: string;
  typeId: string;
  information: string;
}

export function convertListResponse(
  response: ResidentWithNursingOverview[],
  t: TFunction,
): NursingOverviewRow[] {
  const result: NursingOverviewRow[] = [];
  response.forEach((residentData) => {
    const { id: residentId, nursingOverview } = residentData;
    const residentInfo = createResidentInfo(residentData);
    const {
      falls,
      medications,
      wounds,
      pains,
      carePlans,
      nursingReports,
      nutritionProtocols,
      visitSheets,
      vitalSigns,
      vitalSignsHistory,
      socialCareReports,
    } = nursingOverview;
    [
      ...(falls ?? []),
      ...(medications ?? []),
      ...(wounds ?? []),
      ...(pains ?? []),
      ...(carePlans ?? []),
      ...(nursingReports ?? []),
      ...(nutritionProtocols ?? []),
      ...(visitSheets ?? []),
      ...(vitalSigns ?? []),
      ...(vitalSignsHistory ?? []),
      ...(socialCareReports ?? []),
    ]?.forEach((item) => {
      result.push({
        uniqueKey: nanoid(),
        residentId,
        residentInfo,
        date: item.date ?? new Date(),
        type: t(getType(item.type)),
        typeId: item.type ?? '-',
        information: createInformation(item.type, item.information, t),
      });
    });
  });
  return result;
}

export function getSelectedTypeIds(filterValues: FilterValues[]): Array<string> {
  const result: Array<string> = [];
  filterValues.forEach((item) => {
    if (item.value) {
      result.push(item.id);
    }
  });
  return result;
}

export function getAllTypeIds(filterValues: FilterValues[]): Array<string> {
  const result: Array<string> = [];
  filterValues.forEach((i) => {
    result.push(i.id);
  });
  return result;
}

export function filterDataSource(
  data: NursingOverviewRow[],
  filter: Array<string>,
): NursingOverviewRow[] {
  if (data) {
    return data.filter((element) => filter.includes(element.typeId));
  }
  return [];
}

function createResidentInfo(resident: ResidentWithNursingOverview): string {
  const fullName = formatFullName(resident.name);
  const { livingArea, room } = resident;
  if (!livingArea && !room) {
    return fullName;
  } else if (!livingArea && room) {
    return `${fullName}, ${room.name}`;
  } else if (livingArea && !room) {
    return `${fullName}, ${livingArea.name}`;
  }
  return `${fullName}, ${livingArea?.name}, ${room?.name}`;
}

function getType(type: NursingOverviewType | undefined): string {
  switch (type) {
    case NursingOverviewType.CARE_PLAN_FORM_UPDATED:
    case NursingOverviewType.MEDICATION_ADDED:
    case NursingOverviewType.MEDICATION_ENDED:
    case NursingOverviewType.NURSING_REPORT_ADDED:
      return `lists_nursingOverview_type_${type}`;
    case NursingOverviewType.WOUND_ADDED:
    case NursingOverviewType.WOUND_REPORT_ADDED:
      return 'lists_nursingOverview_type_WOUND';
    case NursingOverviewType.PAIN_PROTOCOL_ADDED:
    case NursingOverviewType.PAIN_ASSESSMENT_ADDED:
      return 'lists_nursingOverview_type_PAIN';
    case NursingOverviewType.FALL_PROTOCOL_ADDED:
      return 'lists_nursingOverview_type_FALL';
    case NursingOverviewType.VISIT_SHEET_UPDATED:
      return 'lists_nursingOverview_type_VISIT';
    case NursingOverviewType.VISIT_SHEET_QUESTION:
      return 'lists_nursingOverview_type_VISIT_QUESTION';
    case NursingOverviewType.NUTRITION_PROTOCOL:
      return 'lists_nursingOverview_type_NUTRITION_PROTOCOL';
    case NursingOverviewType.VITAL_SIGN_WEIGHT:
      return 'lists_nursingOverview_type_VITAL_SIGN_WEIGHT';
    case NursingOverviewType.VITAL_SIGN_WEIGHT_BMI:
      return 'lists_nursingOverview_type_VITAL_SIGN_WEIGHT_BMI';
    case NursingOverviewType.VITAL_SIGN_WEIGHT_HISTORY:
      return 'lists_nursingOverview_type_VITAL_SIGN_WEIGHT_HISTORY';
    case NursingOverviewType.SOCIAL_CARE_REPORT:
      return 'lists_nursingOverview_type_SOCIAL_CARE_REPORT';
    default:
      return '-';
  }
}

function createInformation(
  type: NursingOverviewType | undefined,
  information: string[] | undefined,
  t: TFunction,
): string {
  if (!information) return '-';
  switch (type) {
    case NursingOverviewType.MEDICATION_ADDED:
    case NursingOverviewType.MEDICATION_ENDED:
      return `${t(information[2])}</br>${information[1]}, ${information[0]}`;
    case NursingOverviewType.NURSING_REPORT_ADDED:
      return createNursingReportInformation(information, t);
    case NursingOverviewType.WOUND_ADDED:
      return createWoundInformation(information, t);
    case NursingOverviewType.PAIN_PROTOCOL_ADDED:
      return createPainProtocolInformation(information, t);
    case NursingOverviewType.PAIN_ASSESSMENT_ADDED:
      return createPainAssessmentInformation(information, t);
    case NursingOverviewType.FALL_PROTOCOL_ADDED:
      return createFallProtocolInformation(information, t);
    case NursingOverviewType.VISIT_SHEET_UPDATED:
    case NursingOverviewType.VISIT_SHEET_QUESTION:
      return createVisitSheetInformation(information, t);
    case NursingOverviewType.NUTRITION_PROTOCOL:
      return createNutritionProtocolInformation(information, t);
    case NursingOverviewType.VITAL_SIGN_WEIGHT:
      return createVitalSignWeightInformation(information);
    case NursingOverviewType.VITAL_SIGN_WEIGHT_BMI:
      return createVitalSignWeightBMIInformation(information);
    case NursingOverviewType.VITAL_SIGN_WEIGHT_HISTORY:
      return createVitalSignWeightHistoryInformation(information, t);
    case NursingOverviewType.SOCIAL_CARE_REPORT:
      return createSocialCareReportInformation(information, t);
    default:
      return '';
  }
}

function createNursingReportInformation(information: string[], t: TFunction) {
  const topic = `${t('lists_nursingOverview_topic')}: ${information[0]}`;
  const observations = information[1]
    ? `<br />${t('lists_nursingOverview_observations')}: ${information[1]}`
    : '';
  const measures = information[2]
    ? `<br />${t('lists_nursingOverview_measures')}: ${information[2]}`
    : '';
  return `${topic} ${observations} ${measures}`;
}

function createWoundInformation(information: string[], t: TFunction) {
  const documentedAt = `${t('from')} ${dateFormat(new Date(information[1]))}`;
  const type = information[2];
  const latestWoundDescriptionDocumentedAt = `${dateFormat(new Date(information[3]))}`;
  const latestWoundTherapyDate = information[4] ? `${dateFormat(new Date(information[4]))}` : '-';
  const localisation = information[0]
    ? `${t('lists_nursingOverview_localisation')}: ${information[0]}`
    : `${t('lists_nursingOverview_localisation')}: -, `;
  const emergenceLocation = `${t('wound_' + information[5].toLocaleLowerCase())}`;
  return `
    ${t(`${type}`)},
    ${documentedAt},
    ${localisation}
    ${t(emergenceLocation)},
    <br/>${t('lists_nursingOverview_last_woundDescription')} : ${latestWoundDescriptionDocumentedAt}
    <br/>${t('lists_nursingOverview_type_WOUND_THERAPY_ADDED')} : ${latestWoundTherapyDate}`;
}

function createPainProtocolInformation(information: string[], t: TFunction): string {
  return (
    `${t('lists_nursingOverview_type_PAIN_PROTOCOL_ADDED')};` +
    `<br />${t('lists_nursingOverview_localisation')}: ${information[0]}`
  );
}

function createPainAssessmentInformation(information: string[], t: TFunction): string {
  const localisation = information[0]
    ? `${t('lists_nursingOverview_localisation')}: ${information[0]}; <br />`
    : '';
  const inRest = information[1]
    ? `${t('lists_nursingOverview_inRest')}: ${information[1]}; <br />`
    : '';
  const inStress = information[2]
    ? `${t('lists_nursingOverview_inStress')}: ${information[2]}; <br />`
    : '';
  const measures = information[3]
    ? `${t('lists_nursingOverview_measures')}: ${information[3]};`
    : '';
  return `${t(
    'lists_nursingOverview_type_PAIN_ASSESSMENT_ADDED',
  )};</br>${localisation}${inRest}${inStress}${measures}`;
}

function createFallProtocolInformation(information: string[], t: TFunction): string {
  const localisation = information[0]
    ? `<br />${t('lists_nursingOverview_localisation')}: ${information[0]}; `
    : '';
  const measures = information[1]
    ? `<br />${t('lists_nursingOverview_measures')}: ${information[1]}`
    : '';
  return `${t('lists_nursingOverview_type_FALL_PROTOCOL_ADDED')}; ${localisation}${measures}`;
}

function createVisitSheetInformation(information: string[], t: TFunction): string {
  const question = information[0]
    ? `${t('lists_nursingOverview_question')}: ${information[0]} <br />`
    : '';
  const answer = information[1]
    ? `${t('lists_nursingOverview_answer')}: ${information[1]} <br />`
    : '';

  return `${question}${answer}`;
}

function createNutritionProtocolInformation(information: string[], t: TFunction): string {
  const targetCalories = information[0]
    ? `${t('lists_nursingOverview_target_calories')}: ${information[0]} ${t('kcal')}<br />`
    : '';
  const actualCalories = information[1]
    ? `${t('lists_nursingOverview_actual_calories')}: ${information[1]} ${t('kcal')} <br />`
    : '';
  const targetDrinkIntake = information[2]
    ? `${t('lists_nursingOverview_target_drink_intake')}: ${information[2]} ${t('ml')} <br />`
    : '';
  const actualDrinkIntake = information[3]
    ? `${t('lists_nursingOverview_actual_drink_intake')}: ${information[3]} ${t('ml')}<br />`
    : '';
  const totalLiquidOutput = information[4]
    ? `${t('lists_nursingOverview_total_liquid_output')}: ${information[4]} ${t('ml')}<br />`
    : '';

  return `${targetCalories}${actualCalories}${targetDrinkIntake}${actualDrinkIntake}${totalLiquidOutput}`;
}

function createVitalSignWeightInformation(information: string[]): string {
  return `${information[0]} kg`;
}

function createVitalSignWeightBMIInformation(information: string[]): string {
  return `${information[0]} `;
}

function createSocialCareReportInformation(information: string[], t: TFunction): string {
  const topic = `${t('lists_nursingOverview_topic')}: ${information[0]}`;
  const observations = information[1]
    ? `<br />${t('lists_nursingOverview_observations')}: ${information[1]}`
    : '';
  return `${topic} ${observations}`;
}

const weightOutOfRangeTypes = [
  {
    value: WeightOutOfRangeType.BMI_UNDER_19,
    label: 'vital_sign_weight_measurement_out_of_range_bmi',
  },
  {
    value: WeightOutOfRangeType.WEIGHT_DECREASE_6_MONTHS_10_PERCENT,
    label: 'vital_sign_weight_measurement_out_of_range_6_months',
  },
  {
    value: WeightOutOfRangeType.WEIGHT_DECREASE_3_MONTHS_5_PERCENT,
    label: 'vital_sign_weight_measurement_out_of_range_3_months',
  },
  {
    value: WeightOutOfRangeType.WEIGHT_DECREASE_1_MONTH_3_PERCENT,
    label: 'vital_sign_weight_measurement_out_of_range_1_month',
  },
];

function createVitalSignWeightHistoryInformation(information: string[], t: TFunction): string {
  const type = information[0]
    ? `${t(weightOutOfRangeTypes.find((f) => f.value === information[0])?.label ?? '')} <br />`
    : '';
  const reason = information[1]
    ? `${t('vital_sign_weightChangeModal_reason')}: ${information[1]
        .split(',')
        .map((v) => t('vital_sign_weightChangeModal_' + v))
        .join(', ')} <br />`
    : '';
  const measures = information[2]
    ? `${t('vital_sign_weightChangeModal_measures')}: ${information[2]
        .split(',')
        .map((v) => t('vital_sign_weightChangeModal_' + v))
        .join(', ')} <br />`
    : '';
  return `${type}${reason}${measures}`;
}
